import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  RobotOutlined,
  CloseOutlined,
  SendOutlined,
  LoadingOutlined,
  CommentOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { assistantService } from '../../../services/assistant.service';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useTheme } from '../../../contexts/ThemeContext';
const botIcon = 'https://dxy0mpvb3evn5.cloudfront.net/widget/IconoAssistant.webp';

// ─── Types ───────────────────────────────────────────────────────
interface Message {
  id: string;
  role: 'user' | 'bot' | 'error';
  text: string;
  timestamp: Date;
}

interface SupportAssistantProps {
  primaryColor?: string;
}

// ─── Helpers ─────────────────────────────────────────────────────
const generateId = () => `msg_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const getContrastColor = (hex: string): string => {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luminance > 0.45 ? '#1a1a1a' : '#ffffff';
};

// ─── Component ───────────────────────────────────────────────────
const SupportAssistant: React.FC<SupportAssistantProps> = ({
  primaryColor = '#6C3AFF',
}) => {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isFabHidden, setIsFabHidden] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const lastScrollY = useRef(0);
  const { theme } = useTheme();
  const isDarkBg = theme === 'dark';
  const textOnPrimary = getContrastColor(primaryColor);
  const gradient = 'linear-gradient(135deg, #00C9FF 0%, #6C3AFF 70%, #FF6B6B 90%, #FFD93D 100%)';
  const gradientReverse = 'linear-gradient(135deg, #FFD93D 0%, #FF6B6B 10%, #6C3AFF 30%, #00C9FF 100%)';

  // Agregar mensaje de bienvenida al montar
  useEffect(() => {
    if (messages.length === 0) {
      setMessages([
        {
          id: generateId(),
          role: 'bot',
          text: t('supportAssistant.welcomeMessage'),
          timestamp: new Date(),
        },
      ]);
    }
  }, []);

  // Scroll al último mensaje
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // Focus en input al abrir
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen]);

  // Auto-ocultar el FAB al hacer scroll hacia abajo; reaparece al subir o cerca del top
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      if (y < 80) {
        setIsFabHidden(false);
      } else if (y > lastScrollY.current + 6) {
        setIsFabHidden(true);
      } else if (y < lastScrollY.current - 6) {
        setIsFabHidden(false);
      }
      lastScrollY.current = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleSend = useCallback(async () => {
    const text = inputValue.trim();
    if (!text || isLoading) return;

    const userMessage: Message = {
      id: generateId(),
      role: 'user',
      text,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const response = await assistantService.supportAsk(text, language);
      const botMessage: Message = {
        id: generateId(),
        role: 'bot',
        text: response.answer || t('supportAssistant.noResponse'),
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, botMessage]);
    } catch (err: any) {
      const errorText =
        err?.message === 'Message limit reached'
          ? t('supportAssistant.limitReached')
          : t('supportAssistant.errorGeneric');
      setMessages((prev) => [
        ...prev,
        { id: generateId(), role: 'error', text: errorText, timestamp: new Date() },
      ]);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  }, [inputValue, isLoading]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatMessage = (text: string): string => {
    return text
      .replace(/```(\w*)\n?([\s\S]*?)```/g, (_, _lang, code) => {
        const escaped = code.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        return `<div style="background:#1e1e1e;color:#d4d4d4;padding:10px 12px;border-radius:8px;font-family:monospace;font-size:11px;overflow-x:auto;margin:6px 0;white-space:pre-wrap;word-break:break-all">${escaped}</div>`;
      })
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/`([^`]+)`/g, (_, code) => {
        const escaped = code.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        return `<code style="background:#f3f4f6;padding:1px 4px;border-radius:3px;font-size:12px;font-family:monospace">${escaped}</code>`;
      })
      .replace(/^(\d+)\.\s+(.+)$/gm, '<div style="display:flex;gap:6px;margin-left:4px"><span style="color:#9ca3af;flex-shrink:0;font-weight:600">$1.</span><span>$2</span></div>')
      .replace(/^- (.+)$/gm, '<div style="display:flex;gap:6px;margin-left:4px"><span style="color:#9ca3af;flex-shrink:0">•</span><span>$1</span></div>')
      .replace(/\n\n/g, '<div style="height:6px"></div>')
      .replace(/\n/g, '<br/>');
  };

  return (
    <>
      {/* ── Overlay ── */}
      {isOpen && (
        <div
          className="fixed inset-0 z-[99997] bg-black/20 transition-opacity duration-300"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* ── Sidebar Panel ── */}
      <div
        className={`fixed top-0 right-0 z-[99999] h-full w-[400px] max-w-[90vw] flex flex-col bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700 shadow-[-4px_0_24px_rgba(0,0,0,0.1)] transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
      >
        {/* Header */}
        <div
          className="flex items-center gap-3 px-4 py-3.5 flex-shrink-0"
          style={{ background: gradient, color: textOnPrimary }}
        >
          <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
            <RobotOutlined className="text-base" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm leading-tight">{t('supportAssistant.botName')}</div>
            <div className="text-[11px] opacity-80 flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block" />
              {t('supportAssistant.online')}
            </div>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="p-1 opacity-70 hover:opacity-100 transition-opacity"
            style={{ color: textOnPrimary }}
            aria-label={t('supportAssistant.close')}
          >
            <CloseOutlined className="text-base" />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-2.5 bg-gray-50 dark:bg-gray-800/50 scroll-smooth">
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`max-w-[82%] px-3.5 py-2.5 text-[13px] leading-relaxed break-words animate-[fadeInUp_0.2s_ease] ${msg.role === 'user'
                ? 'self-end rounded-xl rounded-br-sm text-white'
                : msg.role === 'error'
                  ? 'self-center bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 rounded-lg text-xs text-center'
                  : 'self-start bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl rounded-bl-sm text-gray-900 dark:text-gray-100'
                }`}
              style={
                msg.role === 'user'
                  ? { background: gradientReverse, color: '#ffffff' }
                  : undefined
              }
              dangerouslySetInnerHTML={
                msg.role === 'bot'
                  ? { __html: formatMessage(msg.text) }
                  : undefined
              }
            >
              {msg.role !== 'bot' ? msg.text : null}
            </div>
          ))}

          {/* Typing indicator */}
          {isLoading && (
            <div className="self-start bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl rounded-bl-sm px-4 py-3 flex gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce [animation-delay:0ms]" />
              <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce [animation-delay:150ms]" />
              <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce [animation-delay:300ms]" />
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="flex items-center gap-2 px-3.5 py-3 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
          <textarea
            ref={inputRef}
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              // Auto-resize
              e.target.style.height = 'auto';
              e.target.style.height = Math.min(e.target.scrollHeight, 80) + 'px';
            }}
            onKeyDown={handleKeyDown}
            placeholder={t('supportAssistant.placeholder')}
            maxLength={2000}
            rows={1}
            className="flex-1 border border-gray-200 dark:border-gray-600 rounded-full px-4 py-2 text-[13px] bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:border-purple-500 dark:focus:border-purple-400 transition-colors resize-none max-h-[80px] leading-snug overflow-y-auto scrollbar-none [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none]"
          />
          <button
            onClick={handleSend}
            disabled={!inputValue.trim() || isLoading}
            className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 transition-transform hover:scale-105 disabled:opacity-40 disabled:cursor-default disabled:transform-none"
            style={{ background: gradient, color: '#ffffff' }}
            aria-label={t('supportAssistant.send')}
          >
            {isLoading ? (
              <LoadingOutlined className="text-sm" />
            ) : (
              <SendOutlined className="text-sm" />
            )}
          </button>
        </div>

        {/* Powered by */}
        <div
          className="text-center py-1.5 text-[10px] bg-white dark:bg-gray-900 flex-shrink-0 font-semibold"
          style={{ backgroundImage: gradient, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}
        >
          Powered by{' '}
          <a
            href="https://kubiy.ai"
            target="_blank"
            rel="noopener noreferrer"
            className="font-bold hover:underline"
          >
            Kubiy
          </a>
        </div>
      </div>

      {/* ── Floating Trigger Button ── */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed right-5 bottom-[calc(1.25rem+env(safe-area-inset-bottom))] z-[99998] border-none cursor-pointer flex items-center justify-center transition-all duration-300 hover:scale-110 p-0 ${
          isOpen ? 'w-14 h-14' : 'w-14 h-14 sm:w-[75px] sm:h-[75px]'
        } ${
          isFabHidden && !isOpen
            ? 'translate-y-28 opacity-0 pointer-events-none'
            : 'translate-y-0 opacity-100'
        }`}
        style={{
          borderRadius: '50%',
          background: isOpen
            ? primaryColor
            : 'linear-gradient(135deg, #00C9FF, #6C3AFF, #FF6B6B, #FFD93D)',
          boxShadow: isOpen
            ? `0 4px 16px ${primaryColor}66`
            : '0 4px 20px rgba(108, 58, 255, 0.4)',
        }}
        aria-label={isOpen ? t('supportAssistant.closeAssistant') : t('supportAssistant.openAssistant')}
      >
        {isOpen ? (
          <CloseOutlined className="text-xl" style={{ color: textOnPrimary }} />
        ) : (
          <div
            style={{
              width: 'calc(100% - 5px)',
              height: 'calc(100% - 5px)',
              borderRadius: '50%',
              backgroundColor: isDarkBg ? '#1a1a2e' : '#ffffff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}
          >
            <img
              src={botIcon}
              alt={t('supportAssistant.openAssistant')}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                filter: 'drop-shadow(0 0 2px rgba(108, 58, 255, 0.6))',
              }}
            />
          </div>
        )}
      </button>
    </>
  );
};

export default SupportAssistant;